import { useQueryClient } from '@tanstack/react-query';
import { useAppParams } from '~/helper/useAppParams';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { FieldValues, Path, UseFormReturn } from 'react-hook-form';
import { shouldAutoRetryAuth } from '~/helper/authAutoRetry.ts';
import { fetchBackend } from '~/helper/fetchBackend.ts';
import { invalidatePropertyRelatedQueries } from '~/helper/invalidatePropertyQueries.ts';

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error' | 'auth_error';

interface UseAutosaveFormOptions<T extends FieldValues> {
  form: UseFormReturn<T>;
  dossierId: string;
  propertyId?: string;
  action: string;
  debounceMs?: number;
  onSaveSuccess?: () => void;
  onSaveError?: (error: Error) => void;
  showToastOnFirstSave?: boolean;
  /**
   * Additional query keys to invalidate after successful save.
   * By default, all loader queries for the dossier/property are invalidated.
   */
  additionalInvalidateKeys?: string[][];
}

interface UseAutosaveFormReturn {
  saveStatus: SaveStatus;
  isDirty: boolean;
  triggerManualSave: () => Promise<void>;
  /**
   * Immediately flush a still-pending debounced change (fire-and-forget).
   * Automatically called on unmount / page-hide, but exposed so callers can
   * force a save before navigating away.
   */
  flushPendingSave: () => void;
  lastSavedAt: Date | null;
  /**
   * If the last save failed, this is the error message to display.
   */
  saveError: Error | null;
  /**
   * Retry a previously failed save.
   */
  retrySave: () => Promise<void>;
}

/**
 * Feature-detect whether the current environment supports the fetch
 * keepalive option. Edge Legacy (Windows 10) does not; Firefox supports
 * it but limits the body to 64 KB. We therefore only enable keepalive for
 * small payloads and fall back to sendBeacon for larger ones.
 */
function supportsFetchKeepalive(): boolean {
  try {
    return 'keepalive' in new Request('about:blank');
  } catch {
    return false;
  }
}

/**
 * Flush a payload using the most reliable available transport:
 * 1. fetch with keepalive (for in-flight navigation, small body)
 * 2. navigator.sendBeacon (works across most modern browsers)
 * 3. synchronous XMLHttpRequest (last-resort for beforeunload on older browsers)
 * 4. normal async fetch (if the page isn't actually hiding)
 *
 * Returns the Response object when a fetch was used; undefined for beacon/XHR.
 */
async function flushWithFallback(
  url: string,
  body: FormData,
  isPageHiding: boolean,
): Promise<Response | undefined> {
  const payloadSize = (() => {
    try {
      let size = 0;
      body.forEach((value) => {
        size +=
          typeof value === 'string'
            ? new Blob([value]).size
            : value instanceof Blob
            ? value.size
            : String(value).length;
      });
      return size;
    } catch {
      return Infinity;
    }
  })();

  if (
    isPageHiding &&
    typeof navigator !== 'undefined' &&
    navigator.sendBeacon
  ) {
    try {
      if (navigator.sendBeacon(url, body)) {
        return undefined;
      }
    } catch {
      // fall through
    }
  }

  if (supportsFetchKeepalive() && payloadSize < 60_000) {
    try {
      const response = await fetch(url, {
        method: 'POST',
        body,
        keepalive: true,
      });
      return response;
    } catch {
      // fall through
    }
  }

  if (isPageHiding && typeof XMLHttpRequest !== 'undefined') {
    try {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', url, false);
      xhr.send(body);
    } catch {
      // last resort failed
    }
    return undefined;
  }

  const response = await fetch(url, { method: 'POST', body });
  return response;
}

export function useAutosaveForm<T extends FieldValues>({
  form,
  dossierId,
  propertyId,
  action,
  debounceMs = 3000,
  onSaveSuccess,
  onSaveError,
  showToastOnFirstSave = false,
  additionalInvalidateKeys = [],
}: UseAutosaveFormOptions<T>): UseAutosaveFormReturn {
  const queryClient = useQueryClient();
  const { contactId } = useAppParams();
  const contactIdRef = useRef(contactId);
  contactIdRef.current = contactId;
  const [
    saveStatus,
    setSaveStatus,
  ] = useState<SaveStatus>('idle');
  const [
    lastSavedAt,
    setLastSavedAt,
  ] = useState<Date | null>(null);
  const [
    saveError,
    setSaveError,
  ] = useState<Error | null>(null);

  // Stable refs that never change identity
  const isMountedRef = useRef(true);
  const editEpochRef = useRef(0);
  const inFlightEpochRef = useRef<number | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const savedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const maxWaitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasAutoSavedRef = useRef(false);
  const formRef = useRef(form);
  const dossierIdRef = useRef(dossierId);
  const propertyIdRef = useRef(propertyId);
  const actionRef = useRef(action);
  const debounceMsRef = useRef(debounceMs);
  const onSaveSuccessRef = useRef(onSaveSuccess);
  const onSaveErrorRef = useRef(onSaveError);
  const showToastOnFirstSaveRef = useRef(showToastOnFirstSave);
  const additionalInvalidateKeysRef = useRef(additionalInvalidateKeys);

  // Keep refs updated with latest values
  formRef.current = form;
  dossierIdRef.current = dossierId;
  propertyIdRef.current = propertyId;
  actionRef.current = action;
  debounceMsRef.current = debounceMs;
  onSaveSuccessRef.current = onSaveSuccess;
  onSaveErrorRef.current = onSaveError;
  showToastOnFirstSaveRef.current = showToastOnFirstSave;
  additionalInvalidateKeysRef.current = additionalInvalidateKeys;

  const isDirty = form.formState.isDirty;

  const buildFormData = (data: T): FormData => {
    const formData = new FormData();
    for (const [
      key,
      value,
    ] of Object.entries(data || {})) {
      if (value !== null) {
        // Boolean values as "1"/"0" for proper backend parsing
        if (typeof value === 'boolean') {
          formData.append(key, value ? '1' : '0');
        } else if (value === undefined) {
          // Send empty string so the backend merge sees the field in sentKeys
          // and can clear the DB value. Without this, a user clearing a number
          // field (e.g. buildingYear → undefined) would be invisible to the
          // merge logic, which would keep the old DB value.
          formData.append(key, '');
        } else {
          formData.append(key, String(value));
        }
      }
    }
    return formData;
  };

  const buildActionUrl = (): string => {
    const params = new URLSearchParams();
    params.append('dossierId', dossierIdRef.current);
    return `/action/${actionRef.current}?${params.toString()}`;
  };

  /**
   * Handles authentication errors by either reloading once (Clerk session
   * propagation race) or redirecting to login. Mirrors the behaviour of
   * useBackendMutation so autosave never silently fails on expired sessions.
   */
  const handleAuthError = () => {
    if (shouldAutoRetryAuth(Date.now(), sessionStorage)) {
      window.location.reload();
      return;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const router = (window as any).__tanstack_router__;
    if (typeof router?.navigate === 'function') {
      void router.navigate({ to: '/auth/login', replace: true });
    }
  };

  const processResponse = async (
    response: Response,
    data: T,
    isAutoSave: boolean,
  ) => {
    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        setSaveStatus('auth_error');
        handleAuthError();
        throw new Error(`FormSubmit auth error: ${response.status}`);
      }
      throw new Error(`FormSubmit: ${response.status} ${response.statusText}`);
    }

    const zodResult = await response.json();

    if (zodResult.success) {
      if (!isMountedRef.current) {
        return;
      }
      formRef.current.reset(data, { keepValues: true });
      setSaveStatus('saved');
      setLastSavedAt(new Date());
      setSaveError(null);

      // Reset 'saved' status back to 'idle' after 3 seconds
      if (savedTimerRef.current) {
        clearTimeout(savedTimerRef.current);
      }
      savedTimerRef.current = setTimeout(() => {
        setSaveStatus('idle');
      }, 3000);

      // Invalidate and refetch all entity-scoped queries in ONE pass
      await invalidatePropertyRelatedQueries(queryClient, {
        dossierId: dossierIdRef.current,
        propertyId: propertyIdRef.current,
        contactId: contactIdRef.current,
      });

      // Invalidate any additional specific keys provided
      for (const key of additionalInvalidateKeysRef.current) {
        await queryClient.invalidateQueries({ queryKey: key });
      }

      onSaveSuccessRef.current?.();
      if (isAutoSave) {
        hasAutoSavedRef.current = true;
      }
    } else {
      for (const [
        key,
        errors,
      ] of Object.entries(zodResult.errors || {})) {
        formRef.current.setError(key as Path<T>, {
          type: 'server',
          message: (errors as string[]).join(', '),
        });
      }
      throw new Error('Validation failed');
    }
  };

  // performSaveRef is defined BEFORE any useEffect uses it
  const performSaveRef = useRef(
    async (
      data: T,
      isAutoSave: boolean = false,
      keepalive: boolean = false,
    ) => {
      // A keepalive flush (unmount / page-hide) must survive teardown, so it
      // runs WITHOUT an abort signal. Normal saves stay cancelable.
      if (!keepalive) {
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
        abortControllerRef.current = new AbortController();
      }

      setSaveStatus('saving');

      try {
        const formData = buildFormData(data);
        const url = buildActionUrl();

        let response: Response | undefined;
        if (keepalive) {
          // Use the most reliable available transport for navigation-time flushes.
          response = await flushWithFallback(url, formData, keepalive);
        } else {
          response = await fetchBackend(url, {
            method: 'POST',
            headers: {},
            body: formData,
            signal: abortControllerRef.current?.signal,
          });
        }

        if (!response) {
          // Beacon / XHR was used — we cannot read a response body, so we
          // cannot confirm the server accepted the data. navigator.sendBeacon
          // returns true as soon as the request enters the browser queue, not
          // when the server responds. Setting 'saved' here would be a silent
          // data-loss lie on 400/500 responses. Instead we keep the status at
          // 'saving' (the user sees a neutral state) and rely on the next
          // visibilitychange or navigation to re-check. The data is queued by
          // the browser and will be sent; if it fails, the next full save
          // cycle will surface the error.
          return;
        }

        await processResponse(response, data, isAutoSave);
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
          return;
        }
        if (!isMountedRef.current) {
          return;
        }
        const normalizedError =
          error instanceof Error ? error : new Error(String(error));
        setSaveStatus((prev) =>
          prev === 'auth_error' ? 'auth_error' : 'error',
        );
        setSaveError(normalizedError);
        onSaveErrorRef.current?.(normalizedError);
      }
    },
  );

  const retrySave = useCallback(async () => {
    editEpochRef.current += 1;
    const epoch = editEpochRef.current;
    if (inFlightEpochRef.current === epoch) {
      return;
    }
    inFlightEpochRef.current = epoch;
    setSaveError(null);
    setSaveStatus('idle');
    try {
      await formRef.current.handleSubmit((data) =>
        performSaveRef.current(data, true),
      )();
    } finally {
      if (inFlightEpochRef.current === epoch) {
        inFlightEpochRef.current = null;
      }
    }
  }, []);

  // Flush a still-pending debounced change immediately. Fire-and-forget with
  // keepalive so the request survives component teardown. Called on unmount
  // (e.g. back-button navigation) and on page hide, guaranteeing a change is
  // saved at the latest when the user navigates away.
  // Dedupe by event source so that different teardown triggers (unmount,
  // beforeunload, pagehide, visibilitychange) each get their own chance to
  // flush. A single global timestamp could block a second flush from a
  // different source if both fire within 1s.
  const lastFlushBySourceRef = useRef<Record<string, number>>({});
  const isPageHidingRef = useRef(false);
  const flushPendingSaveRef = useRef((source: string = 'unknown') => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    // Abort any in-flight non-keepalive request before starting the keepalive
    // flush. Without this, a slow debounce-save could respond out-of-order
    // and overwrite the newer data we are about to send.
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    if (!formRef.current.formState.isDirty && !debounceTimerRef.current) {
      return;
    }
    // Dedupe: only block if the SAME source fired within 1s.
    const now = Date.now();
    if (now - (lastFlushBySourceRef.current[source] ?? 0) < 1000) {
      return;
    }
    lastFlushBySourceRef.current[source] = now;
    console.log('[Autosave] Flushing pending change before teardown');
    formRef.current.handleSubmit((data: T) => {
      performSaveRef.current(data, true, true);
    })();
  });

  // Manual save - uses the same performSaveRef
  const triggerManualSave = useCallback(async () => {
    // Guard against concurrent saves / double clicks across all save paths for identical epoch
    const epoch = editEpochRef.current;
    if (inFlightEpochRef.current === epoch) {
      return;
    }
    inFlightEpochRef.current = epoch;
    // Cancel any pending debounced save to avoid a race with the manual one.
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    // Clear max-wait timer since we are saving immediately
    if (maxWaitTimerRef.current) {
      clearTimeout(maxWaitTimerRef.current);
      maxWaitTimerRef.current = null;
    }
    try {
      await formRef.current.handleSubmit((data) =>
        performSaveRef.current(data, false),
      )();
    } finally {
      if (inFlightEpochRef.current === epoch) {
        inFlightEpochRef.current = null;
      }
    }
  }, []);

  // Setup watch subscription - runs ONLY ONCE on mount
  useEffect(() => {
    isMountedRef.current = true;
    isPageHidingRef.current = false;
    console.log('[Autosave] Setting up watch subscription - ONCE');

    const subscription = formRef.current.watch(
      (
        _value: unknown,
        info: { type?: string; name?: string | string[] } | undefined,
      ) => {
        // React-Hook-Form emits events with info.type === 'change' for native
        // input changes and with undefined/setValue for programmatic updates.
        // We accept both explicit 'change' and undefined types because RHF's
        // Controller onChange reliably produces a change event, while
        // setValue without shouldDirty still carries a defined type and must
        // be ignored. The combination of type !== 'change' AND a truthy info
        // object catches setValue calls.
        if (info && info.type !== 'change') {
          return;
        }

        console.log('[Autosave] Watch event:', info?.name, info?.type);

        // Advance edit epoch for every user modification
        editEpochRef.current += 1;

        // Clear existing timer
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }

        setSaveStatus('idle');
        setSaveError(null);

        // Max-wait throttle: force a save at most every 15s even if the user
        // keeps typing without a pause. Without this, continuous typing resets
        // the debounce timer indefinitely and a browser crash would lose all
        // data. 15s is a pragmatic balance — long enough to batch edits under
        // normal typing, short enough to cap data loss on crash.
        if (!maxWaitTimerRef.current) {
          maxWaitTimerRef.current = setTimeout(async () => {
            maxWaitTimerRef.current = null;
            const epoch = editEpochRef.current;
            if (inFlightEpochRef.current === epoch) {
              return;
            }
            if (formRef.current.formState.isDirty) {
              inFlightEpochRef.current = epoch;
              try {
                await formRef.current.handleSubmit((data: T) =>
                  performSaveRef.current(data, true),
                )();
              } finally {
                if (inFlightEpochRef.current === epoch) {
                  inFlightEpochRef.current = null;
                }
              }
            }
          }, 15_000);
        }

        // Schedule save using current debounceMs from ref
        debounceTimerRef.current = setTimeout(async () => {
          console.log(
            '[Autosave] Timer fired, isDirty:',
            formRef.current.formState.isDirty,
          );
          // Clear max-wait timer since we're about to save anyway
          if (maxWaitTimerRef.current) {
            clearTimeout(maxWaitTimerRef.current);
            maxWaitTimerRef.current = null;
          }
          const epoch = editEpochRef.current;
          if (inFlightEpochRef.current === epoch) {
            return;
          }
          if (formRef.current.formState.isDirty) {
            console.log('[Autosave] Saving...');
            inFlightEpochRef.current = epoch;
            try {
              await formRef.current.handleSubmit((data: T) =>
                performSaveRef.current(data, true),
              )();
            } finally {
              if (inFlightEpochRef.current === epoch) {
                inFlightEpochRef.current = null;
              }
            }
          }
        }, debounceMsRef.current);
      },
    );

    return () => {
      console.log('[Autosave] Cleaning up subscription');
      subscription.unsubscribe();
      // Flush any pending debounced change before the form goes away.
      // Back-button navigation unmounts this component, so this guarantees
      // the change is saved at the latest on navigate-away.
      isPageHidingRef.current = true;
      flushPendingSaveRef.current('unmount');
    };
  }, []); // EMPTY DEPS = runs once on mount only

  // Flush on page hide / tab close / refresh — the router does not unmount
  // the component in these cases, but the page is going away.
  useEffect(() => {
    // isMountedRef.current = true;
    isPageHidingRef.current = false;
    const flushOnBeforeUnload = (event: BeforeUnloadEvent) => {
      isPageHidingRef.current = true;
      flushPendingSaveRef.current('beforeunload');
      // Note: modern browsers ignore returned strings; setting returnValue is
      // only a hint that work is in progress. We do NOT cancel navigation.
      event.returnValue = '';
    };
    const flushOnVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        isPageHidingRef.current = true;
        flushPendingSaveRef.current('visibilitychange');
      }
    };
    const flushOnPageHide = () => {
      isPageHidingRef.current = true;
      flushPendingSaveRef.current('pagehide');
    };

    window.addEventListener('beforeunload', flushOnBeforeUnload);
    document.addEventListener('visibilitychange', flushOnVisibilityChange);
    window.addEventListener('pagehide', flushOnPageHide);

    return () => {
      isMountedRef.current = false;
      window.removeEventListener('beforeunload', flushOnBeforeUnload);
      document.removeEventListener('visibilitychange', flushOnVisibilityChange);
      window.removeEventListener('pagehide', flushOnPageHide);
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (savedTimerRef.current) {
        clearTimeout(savedTimerRef.current);
      }
    };
  }, []);

  return {
    saveStatus,
    isDirty,
    triggerManualSave,
    flushPendingSave: () => flushPendingSaveRef.current(),
    lastSavedAt,
    saveError,
    retrySave,
  };
}
